import { useState } from "react";
import { X, Briefcase, User } from "lucide-react";

export default function CreateListDialog({ open, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("work");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onCreate({ name: trimmed, category });
      setName("");
      setCategory("work");
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-list-overlay"
    >
      <form
        onSubmit={submit}
        className="bg-white border border-zinc-200 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight">New list</h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="create-list-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1.5">List name</span>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="e.g. Q3 Marketing, Side project, Home reno"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
              data-testid="create-list-name-input"
              required
            />
          </label>

          <div>
            <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1.5">Category</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "work", label: "Work", Icon: Briefcase },
                { value: "personal", label: "Personal", Icon: User },
              ].map(({ value, label, Icon }) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setCategory(value)}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 text-sm border transition-colors ${
                    category === value
                      ? "border-zinc-900 bg-zinc-900 text-white"
                      : "border-zinc-300 text-zinc-700 hover:border-zinc-900"
                  }`}
                  data-testid={`create-list-cat-${value}`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <footer className="border-t border-zinc-200 px-5 py-3 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-700 hover:text-zinc-900" data-testid="create-list-cancel">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || submitting}
            className="px-5 py-2 bg-zinc-900 hover:bg-[#002FA7] disabled:opacity-40 text-white text-xs font-medium uppercase tracking-[0.12em] transition-colors"
            data-testid="create-list-submit"
          >
            {submitting ? "Creating…" : "Create list"}
          </button>
        </footer>
      </form>
    </div>
  );
}
